import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, MessageCircle } from 'lucide-react';

import { fetchAddonsPublic, type AddonOferta } from '@/api/client';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { formatPrice } from '@/lib/formatters';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { queryPersistMeta } from '@/lib/pwa/queryPersistence';

export type Intervalo = 'MENSAL' | 'ANUAL';

/** A oferta do add-on do Secretário (pública, como os planos). */
export function useOfertaSecretario() {
  return useQuery<AddonOferta | null>({
    queryKey: [...queryKeys.plans.addons],
    queryFn: async () => (await fetchAddonsPublic()).find((a) => a.codigo === 'secretario') ?? null,
    meta: queryPersistMeta.never,
    staleTime: 10 * 60 * 1000,
  });
}

export function precoDoAddon(oferta: AddonOferta, intervalo: Intervalo): string {
  const centavos = intervalo === 'ANUAL' ? oferta.preco_anual_centavos : oferta.preco_mensal_centavos;
  return `${formatPrice(centavos)}/${intervalo === 'ANUAL' ? 'ano' : 'mês'}`;
}

export function addonDisponivel(oferta: AddonOferta, intervalo: Intervalo): boolean {
  return intervalo === 'ANUAL' ? oferta.disponivel_anual : oferta.disponivel_mensal;
}

/**
 * O card do add-on do Secretário de IA na vitrine (landing). Na tela de Assinatura
 * ele é um card irmão dos planos (``SecretarioAddonPlanCard``).
 *
 * ``nota`` diz o que vale para quem está olhando (trial, cortesia, contratado);
 * ``children`` é a ação da tela (contratar, cancelar, incluir no checkout).
 */
export function SecretarioAddonCard({
  oferta,
  intervalo,
  nota,
  children,
}: {
  oferta: AddonOferta;
  intervalo: Intervalo;
  nota?: ReactNode;
  children?: ReactNode;
}) {
  const itens = [
    `${oferta.atendimentos_max} triagens por mês`,
    'Atualizações de processo ilimitadas',
    'Junto com o Básico ou o Premium IA',
  ];
  return (
    <Card className="border-primary/20">
      <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
            <MessageCircle className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-card-foreground">Secretário de IA</h3>
              <Badge variant="secondary">Add-on</Badge>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Atende seus clientes pelo WhatsApp: acolhe os casos novos, recolhe os documentos e responde quem
              pergunta do processo.
            </p>
            <ul className="mt-3 flex flex-col gap-1.5">
              {itens.map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-card-foreground">
                  <CheckCircle className="h-4 w-4 shrink-0 text-primary" />
                  {item}
                </li>
              ))}
            </ul>
            {nota && <p className="mt-3 text-xs text-muted-foreground">{nota}</p>}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-start gap-3 sm:items-end">
          <span className="text-xl font-bold text-card-foreground">+ {precoDoAddon(oferta, intervalo)}</span>
          {children}
        </div>
      </CardContent>
    </Card>
  );
}
